import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { ACCESS_TOKEN } from '@/utils/user/constants';
import { useEffect } from 'react';

import './style.less';

const CHAT_WIDGET_ID = 'qigger-chat-widget';
const CHAT_WIDGET_STYLE_ID = 'ultracar-chat-widget-button-style';
const CHAT_WIDGET_BUTTON_SELECTOR =
  'div.fixed.bottom-4.right-4.flex.items-center';
const CHAT_WIDGET_TEXT_SELECTOR = '.leading-tight.select-none.text-left';
const CHAT_WIDGET_COLLAPSE_DELAY_MS = 3000;
const CHAT_WIDGET_SHADOW_ROOT_CAPTURE_TIMEOUT_MS = 10000;

const SUPPORT_BUTTON_WRAPPER_CLASS = 'support-button-wrapper';
const SUPPORT_BUTTON_SURFACE_CLASS = 'support-button__surface';
const SUPPORT_BUTTON_CLASS = 'support-button';
const SUPPORT_BUTTON_ICON_CLASS = 'support-button__icon';
const SUPPORT_BUTTON_TEXT_CLASS = 'support-button__text';
const SUPPORT_BUTTON_EXPANDED_CLASS = 'support-button--expanded';
const SUPPORT_BUTTON_COLLAPSED_CLASS = 'support-button--collapsed';

function setSupportButtonExpanded(button: Element, expanded: boolean) {
  button.classList.toggle(SUPPORT_BUTTON_EXPANDED_CLASS, expanded);
  button.classList.toggle(SUPPORT_BUTTON_COLLAPSED_CLASS, !expanded);
}

function getSupportButtonStyleText() {
  const cssTexts: string[] = [];

  Array.from(document.styleSheets).forEach((styleSheet) => {
    let rules: CSSRuleList;

    try {
      rules = styleSheet.cssRules;
    } catch {
      return;
    }

    Array.from(rules).forEach((rule) => {
      if (rule.cssText.includes('support-button')) {
        cssTexts.push(rule.cssText);
      }
    });
  });

  return cssTexts.join('\n');
}

function injectChatWidgetButtonStyle(shadowRoot: ShadowRoot) {
  if (shadowRoot.querySelector(`#${CHAT_WIDGET_STYLE_ID}`)) return;

  const style = document.createElement('style');
  style.id = CHAT_WIDGET_STYLE_ID;
  style.textContent = getSupportButtonStyleText();
  shadowRoot.appendChild(style);
}

function setupChatWidgetButton(shadowRoot: ShadowRoot) {
  injectChatWidgetButtonStyle(shadowRoot);

  const registeredButtons = new WeakSet<Element>();
  const collapseTimers = new Set<number>();

  const registerButton = (wrapper: Element) => {
    if (registeredButtons.has(wrapper)) return;

    const surface = wrapper.firstElementChild;
    const button = surface?.querySelector('button');

    if (!(wrapper instanceof HTMLElement) || !surface || !button) return;

    const text = button.querySelector(CHAT_WIDGET_TEXT_SELECTOR);
    const icon = Array.from(button.children).find((child) => child !== text);

    wrapper.classList.add(SUPPORT_BUTTON_WRAPPER_CLASS);
    surface.classList.add(SUPPORT_BUTTON_SURFACE_CLASS);
    button.classList.add(SUPPORT_BUTTON_CLASS);
    icon?.classList.add(SUPPORT_BUTTON_ICON_CLASS);
    text?.classList.add(SUPPORT_BUTTON_TEXT_CLASS);
    button.setAttribute('aria-label', 'Abrir suporte Ultracar');

    setSupportButtonExpanded(wrapper, true);
    setSupportButtonExpanded(button, true);
    registeredButtons.add(wrapper);

    const collapseTimer = window.setTimeout(() => {
      setSupportButtonExpanded(wrapper, false);
      setSupportButtonExpanded(button, false);
      collapseTimers.delete(collapseTimer);
    }, CHAT_WIDGET_COLLAPSE_DELAY_MS);

    collapseTimers.add(collapseTimer);
  };

  const registerButtons = () => {
    shadowRoot
      .querySelectorAll(CHAT_WIDGET_BUTTON_SELECTOR)
      .forEach(registerButton);
  };

  registerButtons();

  const observer = new MutationObserver(registerButtons);
  observer.observe(shadowRoot, { childList: true, subtree: true });

  return () => {
    observer.disconnect();
    collapseTimers.forEach((timer) => window.clearTimeout(timer));
    collapseTimers.clear();
  };
}

function captureChatWidgetShadowRoot(
  onCapture: (shadowRoot: ShadowRoot) => void,
) {
  const originalAttachShadow = Element.prototype.attachShadow;

  const patchedAttachShadow = function attachShadow(
    this: Element,
    init: ShadowRootInit,
  ) {
    const shadowRoot = originalAttachShadow.call(this, init);

    if (this instanceof HTMLElement && this.id === CHAT_WIDGET_ID) {
      onCapture(shadowRoot);
    }

    return shadowRoot;
  } as typeof Element.prototype.attachShadow;

  Element.prototype.attachShadow = patchedAttachShadow;

  return () => {
    if (Element.prototype.attachShadow === patchedAttachShadow) {
      Element.prototype.attachShadow = originalAttachShadow;
    }
  };
}

export default function ChatAI() {
  const { data: matrix } = useMatrix();

  useEffect(() => {
    if (!matrix?.matrixSettings.isChatSupportEnabled) return undefined;

    let hasRequestedWidgetMount = false;
    let clearButtonSetup: (() => void) | undefined;
    let restoreShadowRootCapture: (() => void) | undefined;
    let restoreShadowRootCaptureTimer: number | undefined;

    const clearShadowRootCapture = () => {
      restoreShadowRootCapture?.();
      restoreShadowRootCapture = undefined;

      if (restoreShadowRootCaptureTimer) {
        window.clearTimeout(restoreShadowRootCaptureTimer);
        restoreShadowRootCaptureTimer = undefined;
      }
    };

    const qiggerConfigScript = document.createElement('script');
    qiggerConfigScript.innerHTML = `window.QiggerChatConfig = {
      apiBaseUrl: '${process.env.REACT_APP_API_URL}/chat-api/v1/',
      widgetUrl: 'https://storageultracarsupportai.blob.core.windows.net/chat-files/widget.js'
    };`;

    const loaderIIFEScript = document.createElement('script');
    loaderIIFEScript.src =
      'https://storageultracarsupportai.blob.core.windows.net/chat-files/loader.iife.js';
    loaderIIFEScript.crossOrigin = 'anonymous';

    const ensureInit = (tries = 10) => {
      const tick = () => {
        const q = window.QiggerChat;
        if (
          q &&
          typeof q.isInitialized === 'function' &&
          typeof q.init === 'function'
        ) {
          if (!q.isInitialized()) q.init();
          return;
        }
        if (tries-- > 0) setTimeout(tick, 100);
      };
      tick();
    };

    const mountWidget = () => {
      const hasAccessToken = !!localStorage.getItem(ACCESS_TOKEN);

      if (
        !hasAccessToken ||
        hasRequestedWidgetMount ||
        document.getElementById(CHAT_WIDGET_ID)
      ) {
        return;
      }

      hasRequestedWidgetMount = true;
      clearButtonSetup?.();
      clearShadowRootCapture();

      restoreShadowRootCapture = captureChatWidgetShadowRoot((shadowRoot) => {
        clearButtonSetup = setupChatWidgetButton(shadowRoot);
        clearShadowRootCapture();
      });
      restoreShadowRootCaptureTimer = window.setTimeout(
        clearShadowRootCapture,
        CHAT_WIDGET_SHADOW_ROOT_CAPTURE_TIMEOUT_MS,
      );

      document.body.append(qiggerConfigScript, loaderIIFEScript);

      ensureInit();
    };

    const unmountWidget = () => {
      hasRequestedWidgetMount = false;
      qiggerConfigScript?.remove();
      loaderIIFEScript?.remove();
      document.getElementById(CHAT_WIDGET_ID)?.remove();
      clearButtonSetup?.();
      clearButtonSetup = undefined;
      clearShadowRootCapture();
    };

    const onStorage = (e: StorageEvent) => {
      if (e.storageArea?.getItem(ACCESS_TOKEN)) {
        mountWidget();
      } else {
        unmountWidget();
      }
    };

    mountWidget();

    window.addEventListener('DOMContentLoaded', mountWidget);
    window.addEventListener('USER_LOGOUT', unmountWidget);
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('DOMContentLoaded', mountWidget);
      window.removeEventListener('USER_LOGOUT', unmountWidget);
      window.removeEventListener('storage', onStorage);
      unmountWidget();
    };
  }, [matrix?.matrixSettings.isChatSupportEnabled]);

  return null;
}
